import React, { useState, useEffect, useRef } from 'react';
import { ButtonWidgetConfig } from '../../Canvas/Widgets/ButtonWidget/ButtonWidget';
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';
import { InfoIcon } from 'assests/GmsIcons';
import Tooltip from '../../../../../../../Components/GMS/Rules/Components/Tooltip';

interface ButtonWidgetConfigPanelProps {
  id?: string;
  config: ButtonWidgetConfig;
  onConfigChange: (config: ButtonWidgetConfig) => void;
}

const ButtonWidgetConfigPanel = ({ config, onConfigChange }: ButtonWidgetConfigPanelProps) => {
  const { saveWidgetConfig } = useComponentRegistry();

  const displayNameDebounceRef = useRef<NodeJS.Timeout>();
  const classNameDebounceRef = useRef<NodeJS.Timeout>();
  const functionNameDebounceRef = useRef<NodeJS.Timeout>();

  const [localDisplayName, setLocalDisplayName] = useState(config.displayName);
  const [localClassName, setLocalClassName] = useState(config.className);
  const [localFunctionName, setLocalFunctionName] = useState(config.functionName);

  useEffect(() => {
    setLocalDisplayName(config.displayName);
  }, [config.displayName]);

  useEffect(() => {
    setLocalClassName(config.className);
  }, [config.className]);

  useEffect(() => {
    setLocalFunctionName(config.functionName);
  }, [config.functionName]);

  useEffect(() => {
    return () => {
      if (displayNameDebounceRef.current) clearTimeout(displayNameDebounceRef.current);
      if (classNameDebounceRef.current) clearTimeout(classNameDebounceRef.current);
      if (functionNameDebounceRef.current) clearTimeout(functionNameDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (config) {
      saveWidgetConfig('button-widget', config);
    }
  }, [config, saveWidgetConfig]);

  const handleChange = (key: keyof ButtonWidgetConfig, value: string) => {
    onConfigChange({
      ...config,
      [key]: value
    });
  };

  const handleDebouncedDisplayNameChange = (value: string) => {
    setLocalDisplayName(value);
    if (displayNameDebounceRef.current) clearTimeout(displayNameDebounceRef.current);
    displayNameDebounceRef.current = setTimeout(() => {
      handleChange('displayName', value);
    }, 500);
  };

  const handleDebouncedClassNameChange = (value: string) => {
    setLocalClassName(value);
    if (classNameDebounceRef.current) clearTimeout(classNameDebounceRef.current);
    classNameDebounceRef.current = setTimeout(() => {
      handleChange('className', value);
    }, 500);
  };

  const handleDebouncedFunctionNameChange = (value: string) => {
    setLocalFunctionName(value);
    if (functionNameDebounceRef.current) clearTimeout(functionNameDebounceRef.current);
    functionNameDebounceRef.current = setTimeout(() => {
      handleChange('functionName', value);
    }, 500);
  };

  return (
    <div className="tw-space-y-6">
      <div className="tw-p-3">
        {/* Widget Header with 3-dot menu */}
        <div className="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-200">
          <h3 className="tw-text-base tw-font-semibold tw-text-[#172B4D]">Widget</h3>
          <button className="tw-text-gray-400 hover:tw-text-gray-600">
            <svg className="tw-w-5 tw-h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>

        {/* Display Name Field (Required) */}
        <div className="tw-mt-3">
          <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
            Display Name <span className="tw-text-red-500">*</span>
          </label>
          <input
            type="text"
            value={localDisplayName}
            onChange={(e) => handleDebouncedDisplayNameChange(e.target.value)}
            className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
            placeholder="Button"
          />
        </div>

        {/* Action Type Section Header */}
        <div className="tw-mt-4">
          <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-font-semibold">
            Action Type <span className="tw-text-red-500">*</span>
          </label>
        </div>

        {/* Class Field with Tooltip */}
        <div className="tw-mt-3">
          <label className="tw-flex tw-items-center tw-gap-1 tw-text-[#6D788C] tw-text-sm tw-mb-2">
            Class
            <Tooltip text="The specified class(es) will be added to the HTML button element's class attribute: <button class='your-classes-here'>.">
              <InfoIcon />
            </Tooltip>
          </label>
          <textarea
            value={localClassName}
            onChange={(e) => handleDebouncedClassNameChange(e.target.value)}
            className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-min-h-[60px] tw-resize-y"
            placeholder="Enter or paste class"
          />
        </div>

        {/* Function Field with Tooltip */}
        <div className="tw-mt-3">
          <label className="tw-flex tw-items-center tw-gap-1 tw-text-[#6D788C] tw-text-sm tw-mb-2">
            Function
            <Tooltip text="The specified function will be called on button click: <button onclick='your-function-here'>.">
              <InfoIcon />
            </Tooltip>
          </label>
          <textarea
            value={localFunctionName}
            onChange={(e) => handleDebouncedFunctionNameChange(e.target.value)}
            className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-min-h-[60px] tw-resize-y"
            placeholder="Enter or paste function"
          />
        </div>
      </div>
    </div>
  );
};

export default ButtonWidgetConfigPanel;
